/**
 * Composes the standings chart, legend, playback timeline, and matchday detail card,
 * owning the local interaction state (line highlight, selected matchday).
 */
import { type ReactNode } from 'react';
import { useBreakpointValue, Box, Text, VStack, useColorModeValue } from '@chakra-ui/react';

import { StandingsChart } from './StandingsChart';
import { StandingsChartLegend } from './StandingsChartLegend';
import { PlaybackControls } from './PlaybackControls';
import { MatchdayDetailCard } from './MatchdayDetailCard';
import {
  useStandingsChartPanel,
  type StandingsChartPanelData,
} from './useStandingsChartPanel';

export interface StandingsChartPanelProps extends StandingsChartPanelData {
  /** When true, omit inner bordered boxes (content sits inside an outer profile card). */
  embedded?: boolean;
  /** Render only the chart block, only matchday, or the full stacked panel. */
  section?: 'full' | 'chart' | 'matchday';
}

function ChartBlock(props: {
  data: StandingsChartPanelData;
  panel: ReturnType<typeof useStandingsChartPanel>;
  embedded: boolean;
}) {
  const { data, panel, embedded } = props;
  const cardBg = useColorModeValue('surfaceCard', 'surfaceCard');
  const border = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');

  const helperText = useBreakpointValue({
    base: 'Tap lines or names to compare users head-to-head. Tap on matchday on the chart for details.',
    md: 'Click lines or names to compare users head-to-head. Click on matchday on the chart for details.',
  });

  const chartInner = (
    <>
      <StandingsChart
        series={data.series}
        maxMatchday={data.maxMatchday}
        visibleThrough={panel.playback.visibleThrough}
        selectedUids={panel.selectedUids}
        hoveredUid={panel.hoveredUid}
        currentUid={data.currentUid}
        selectedMatchday={panel.selectedMatchday}
        colorIndex={panel.colorIndex}
        onHoverUid={panel.setHoveredUid}
        onClickUid={panel.toggleSelected}
        onSelectMatchday={panel.setSelectedMatchday}
      />
      <Box mt={2}>
        <PlaybackControls playback={panel.playback} maxMatchday={data.maxMatchday} />
      </Box>
    </>
  );

  return (
    <VStack align='stretch' spacing={embedded ? 3 : 4} w='full' flex={embedded ? 1 : undefined} minH={0}>
      {embedded ? (
        chartInner
      ) : (
        <Box bg={cardBg} borderWidth='1px' borderColor={border} borderRadius='lg' p={{ base: 2, md: 4 }}>
          {chartInner}
        </Box>
      )}

      <Box flexShrink={0}>
        <Text fontSize='xs' color='gray.500' mb={1}>
          {helperText}
        </Text>
        <StandingsChartLegend
          series={data.series}
          colorIndex={panel.colorIndex}
          selectedUids={panel.selectedUids}
          currentUid={data.currentUid}
          onHoverUid={panel.setHoveredUid}
          onClickUid={panel.toggleSelected}
          onClearSelection={panel.clearSelection}
        />
      </Box>
    </VStack>
  );
}

function MatchdayBlock(props: {
  data: StandingsChartPanelData;
  panel: ReturnType<typeof useStandingsChartPanel>;
  embedded: boolean;
}) {
  const { data, panel, embedded } = props;

  if (panel.selectedMatchday < 1) {
    return null;
  }

  return (
    <MatchdayDetailCard
      matchday={panel.selectedMatchday}
      series={data.series}
      columns={data.columns}
      colorIndex={panel.colorIndex}
      currentUid={data.currentUid}
      gameResults={data.gameResults}
      embedded={embedded}
    />
  );
}

export function StandingsChartPanel(props: StandingsChartPanelProps) {
  const { embedded = false, section = 'full', ...data } = props;
  const panel = useStandingsChartPanel(data);

  if (section === 'chart') {
    return <ChartBlock data={data} panel={panel} embedded={embedded} />;
  }

  if (section === 'matchday') {
    return <MatchdayBlock data={data} panel={panel} embedded={embedded} />;
  }

  return (
    <VStack align='stretch' spacing={4} w='full'>
      <ChartBlock data={data} panel={panel} embedded={embedded} />
      <MatchdayBlock data={data} panel={panel} embedded={embedded} />
    </VStack>
  );
}

/** Shares chart interaction state across separate layout regions (e.g. AI League desktop grid). */
export function StandingsChartPanelSplit(props: {
  embedded?: boolean;
  children: (sections: { chartSection: ReactNode; matchdaySection: ReactNode }) => ReactNode;
} & StandingsChartPanelData) {
  const { embedded = false, children, ...data } = props;
  const panel = useStandingsChartPanel(data);

  return (
    <>
      {children({
        chartSection: <ChartBlock data={data} panel={panel} embedded={embedded} />,
        matchdaySection: <MatchdayBlock data={data} panel={panel} embedded={embedded} />,
      })}
    </>
  );
}

export { useStandingsChartPanel };
